import React, { useEffect, useMemo, useRef, useState } from "react";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import useTheme from "@common/hooks/useTheme";
import {
  checkLabel,
  passedChecks,
  todoGroups,
  todoItems,
} from "../helpers/reviewItems";
import useStyles from "../styles/useStyles";
import { SaveResult, ScopeReview, TodoKind } from "../types";

interface Props {
  review: ScopeReview;
  canManage: boolean;
  saveCompleted: (ids: string[]) => Promise<SaveResult>;
}

const KIND_TEXT: Record<TodoKind, string> = {
  placeholder: "Placeholder",
  failed: "Failed",
  warning: "Warning",
};

const CONFLICT_TOAST =
  "This review was refreshed elsewhere; your checkmarks were reset.";
const ERROR_TOAST = "Your checkmark could not be saved.";

const TodoCard: React.FC<Props> = ({ review, canManage, saveCompleted }) => {
  const styles = useStyles();
  const theme = useTheme();
  const items = useMemo(() => todoItems(review), [review]);
  const groups = useMemo(() => todoGroups(items), [items]);
  const passed = useMemo(() => passedChecks(review), [review]);

  const seed = () => {
    const ids = new Set(items.map((i) => i.id));
    return new Set(
      (review.completed_item_ids ?? []).filter((id) => ids.has(id))
    );
  };

  const [completed, setCompleted] = useState<Set<string>>(seed);
  const latest = useRef(completed);
  const [seededAt, setSeededAt] = useState(review.generated_at);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [showPassed, setShowPassed] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Re-seed from the server whenever a new review is generated.
  useEffect(() => {
    if (seededAt !== review.generated_at) {
      const next = seed();
      latest.current = next;
      setCompleted(next);
      setSeededAt(review.generated_at);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review.generated_at]);

  const toggle = async (id: string) => {
    const before = latest.current;
    const next = new Set(before);
    const wasChecked = next.has(id);
    if (wasChecked) next.delete(id);
    else next.add(id);
    latest.current = next;
    setCompleted(next);

    const result = await saveCompleted(Array.from(next));
    if (result === "conflict") {
      // Drop the optimistic state; the refetched review re-seeds on its new generated_at.
      const reset = seed();
      latest.current = reset;
      setCompleted(reset);
      setToast(CONFLICT_TOAST);
    } else if (result === "error") {
      // Undo only this toggle so later in-flight toggles are kept.
      const reverted = new Set(latest.current);
      if (wasChecked) reverted.add(id);
      else reverted.delete(id);
      latest.current = reverted;
      setCompleted(reverted);
      setToast(ERROR_TOAST);
    }
  };

  const pillColor: Record<TodoKind, string> = {
    placeholder: theme.colors.navy["3"],
    failed: "#B90200",
    warning: "#CC672F",
  };

  return (
    <div className={styles.card}>
      {toast && (
        <ToastAlert
          variant="warning"
          text={toast}
          onClose={() => setToast(null)}
        />
      )}
      <div className={styles.todoHeader}>
        <h2>To-Do</h2>
        {items.length > 0 && (
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setHideCompleted((v) => !v)}
          >
            {hideCompleted ? "Show Completed" : "Hide Completed"}
          </button>
        )}
      </div>
      {items.length === 0 && <p>Nothing to do. Every check passed.</p>}
      {groups.map((group) => {
        const rows = group.items.filter(
          (i) => !(hideCompleted && completed.has(i.id))
        );
        if (rows.length === 0) return null;
        return (
          <div key={group.category}>
            <div className={styles.todoCategory}>{group.category}</div>
            {rows.map((item) => {
              const done = completed.has(item.id);
              return (
                <div key={item.id} className={styles.todoRow}>
                  <input
                    type="checkbox"
                    id={`todo-${item.id}`}
                    checked={done}
                    disabled={!canManage}
                    onChange={() => toggle(item.id)}
                  />
                  <span
                    className={styles.pill}
                    style={{ background: pillColor[item.kind] }}
                  >
                    {KIND_TEXT[item.kind]}
                  </span>
                  <label
                    htmlFor={`todo-${item.id}`}
                    className={done ? styles.todoDone : undefined}
                  >
                    {item.kind === "placeholder"
                      ? `${item.label}: "${item.detail}"`
                      : item.label}
                  </label>
                </div>
              );
            })}
          </div>
        );
      })}
      {passed.length > 0 && (
        <div>
          <button
            type="button"
            className={styles.linkButton}
            style={{ marginTop: 16 }}
            onClick={() => setShowPassed((v) => !v)}
          >
            {`${showPassed ? "▾" : "▸"} ${passed.length} Passed Checks`}
          </button>
          {showPassed &&
            passed.map((check) => (
              <div key={check.id} className={styles.todoRow}>
                {checkLabel(check)}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default TodoCard;
